import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { BillService } from '../../services/bill.service';
import { HierarchyAddressRowComponent } from './hierarchy-address-row.component';
import { MatchStatusBadgeComponent } from '../shared/match-status-badge.component';
import { ColumnKey, BillGroup, BillStatus, BillFile } from '../../models';

@Component({
  selector: 'app-hierarchy-item',
  standalone: true,
  imports: [
    DecimalPipe,
    MatCheckboxModule,
    MatchStatusBadgeComponent,
    HierarchyAddressRowComponent
],
  template: `
      <div
        class="hierarchy-item"
        [class.drag-over]="isDragOver"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
        >
        <!-- Entity Header -->
        <div class="entity-header" (click)="toggleExpand($event)">
          <div class="entity-info">
            <mat-checkbox
              [checked]="billService.selectedEntityIds().includes(item.name)"
              (change)="toggleSelection()"
              (click)="$event.stopPropagation()"
            ></mat-checkbox>
      
            <button class="expand-btn" (click)="toggleExpand($event)" [class.collapsed]="!expanded">
              <i class="material-symbols-rounded">expand_more</i>
            </button>
      
            <div class="entity-icon-wrapper">
              <i class="material-symbols-rounded entity-icon">account_balance</i>
            </div>
      
            <div class="entity-details">
              <div class="entity-name-wrapper">
                <div class="entity-name-line">
                  <h3 class="entity-name">
                    @if (item.isLoading && isUnknownEntity()) {
                      <span class="loading-text">
                        {{ item.loadingBillCount === 1 ? 'Reading bill' : 'Reading bills' }}
                        <i class="material-symbols-rounded spinner">sync</i>
                      </span>
                    } @else {
                      {{ item.name }}
                      @if (isUnknownEntity()) {
                        <span class="unknown-label">Unknown Entity</span>
                      }
                    }
                  </h3>
                  <app-match-status-badge [status]="'NEW'"></app-match-status-badge>
                </div>
              </div>
            </div>

          </div>

          <div class="entity-stats">
            <span class="stat"><i class="material-symbols-rounded">home_work</i> {{ addressCount }}</span>
            <span class="stat"><i class="material-symbols-rounded">speed</i> {{ meterCount }}</span>
            <span class="stat"><i class="material-symbols-rounded">description</i> {{ item.billCount }}</span>
            @if (item.metrics.electric > 0) {
              <span class="stat usage">{{ item.metrics.electric | number }} kWh</span>
            }
            @if (item.metrics.gas > 0) {
              <span class="stat usage">{{ item.metrics.gas | number }} DTH</span>
            }
            @if (processedCount > 0) {
              <span class="stat-badge processed">{{ processedCount }} PROCESSED</span>
            }
          </div>
        </div>
      
        <!-- Addresses (Children) -->
        <div class="addresses-container" [class.expanded]="expanded">
          <div class="addresses-content">
            @for (address of getAddresses(); track address) {
              <app-hierarchy-address-row
                [address]="address"
                [visibleColumns]="visibleColumns"
                [sortConfig]="sortConfig"
                (openPairReview)="openPairReview.emit($event)"
              ></app-hierarchy-address-row>
            }
          </div>
        </div>
      </div>
      `,
  styleUrl: './hierarchy-item.component.scss'
})
export class HierarchyItemComponent implements OnChanges {
    @Input() item: any;
    @Input() visibleColumns: Record<ColumnKey, boolean> = {} as any;
    @Input() sortConfig: any;
    @Output() openPairReview = new EventEmitter<BillGroup>();

    expanded = true;
    isDragOver = false;

    // Pre-computed stats
    addressCount = 0;
    meterCount = 0;
    processedCount = 0;
    addresses: any[] = [];

    constructor(public billService: BillService) {}

    ngOnChanges() {
        this.updateComputedValues();
    }

    private updateComputedValues() {
        this.addresses = Object.values(this.item.addresses || {});
        this.addressCount = this.addresses.length;
        this.meterCount = this.addresses.reduce((sum: number, addr: any) =>
            sum + Object.keys(addr.meters || {}).length, 0);

        // Count processed bills across all meters
        let processed = 0;
        this.addresses.forEach((addr: any) => {
            Object.values(addr.meters || {}).forEach((meter: any) => {
                (meter.bills || []).forEach((bill: BillFile) => {
                    if (bill.status === BillStatus.APPROVED || bill.status === BillStatus.REVIEW_NEEDED) processed++;
                });
            });
        });
        this.processedCount = processed;
    }

    isUnknownEntity(): boolean {
        return !this.item.name?.trim() || this.item.name === 'Unknown Entity';
    }

    getAddresses() {
        return this.addresses;
    }

    toggleExpand(e: Event) {
        if ((e.target as HTMLElement).tagName !== 'INPUT') {
            this.expanded = !this.expanded;
            e.stopPropagation();
        }
    }

    toggleSelection() {
        this.billService.toggleSelection(this.item.name, 'entity');
    }

    onDragOver(e: DragEvent) {
        e.preventDefault();
        if (e.dataTransfer?.types.includes('address-id')) {
            this.isDragOver = true;
        }
    }

    onDragLeave(e: DragEvent) {
        this.isDragOver = false;
    }

    onDrop(e: DragEvent) {
        e.preventDefault();
        this.isDragOver = false;

        // Handle address being dropped onto this entity
        const addressId = e.dataTransfer?.getData('address-id');
        if (addressId) {
            this.billService.moveAddressToEntity(addressId, this.item.name);
        }
    }
}